import * as TabsPrimitive from "@radix-ui/react-tabs";

import { cn } from "@repo/ui/lib/utils";
import { ComponentPropsWithoutRef, ElementRef, forwardRef } from "react";
import { cva, VariantProps } from "class-variance-authority";

const tabVariants = cva("inline-flex items-center justify-center", {
  variants: {
    variant: {
      default:
        "rounded-[10px] bg-secondary-gray2 dark:bg-secondary-black p-[4px]",
      ghost: "gap-[40px]",
    },
  },
  defaultVariants: {
    variant: "default",
  },
});

const triggerVariants = cva(
  "inline-flex items-center justify-center whitespace-nowrap ring-offset-white transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-950 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        default:
          "bg-secondary-gray2 dark:bg-secondary-black text-secondary-gray1 text-[16px]  rounded-[6px] px-[30px] py-[10px] font-normal data-[state=active]:font-bold data-[state=active]:bg-secondary-black data-[state=active]:text-primary-white dark:data-[state=active]:bg-secondary-gray1 dark:data-[state=active]:bg-opacity-50 dark:data-[state=active]:text-slate-50",
        ghost:
          "relative mb-[20px] text-secondary-black data-[state=active]:text-primary-black dark:text-secondary-gray1 dark:data-[state=active]:text-primary-white data-[state=active]:font-bold after:bg-primary-black dark:after:bg-primary-white after:absolute after:mt-[45px] data-[state=active]:after:h-[10px] data-[state=active]:after:w-[10px] data-[state=active]:after:rounded-full data-[state=active]:after:inline-block",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
);

const Tabs = TabsPrimitive.Root;

const TabsList = forwardRef<
  ElementRef<typeof TabsPrimitive.List>,
  ComponentPropsWithoutRef<typeof TabsPrimitive.List> &
    VariantProps<typeof tabVariants>
>(({ className, variant, ...props }, ref) => (
  <TabsPrimitive.List
    ref={ref}
    className={cn(tabVariants({ variant, className }))}
    {...props}
  />
));
TabsList.displayName = TabsPrimitive.List.displayName;

const TabsTrigger = forwardRef<
  ElementRef<typeof TabsPrimitive.Trigger>,
  ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger> &
    VariantProps<typeof triggerVariants>
>(({ className, variant, ...props }, ref) => (
  <TabsPrimitive.Trigger
    ref={ref}
    className={cn(triggerVariants({ variant, className }))}
    {...props}
  />
));
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName;

const TabsContent = forwardRef<
  ElementRef<typeof TabsPrimitive.Content>,
  ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Content
    ref={ref}
    className={cn(
      "mt-2 ring-offset-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-950 focus-visible:ring-offset-2 dark:ring-offset-slate-950 dark:focus-visible:ring-slate-300",
      className,
    )}
    {...props}
  />
));
TabsContent.displayName = TabsPrimitive.Content.displayName;

export { Tabs, TabsList, TabsTrigger, TabsContent };
